import type { TFunction } from "i18next";
import type { JSX } from "react";
import {
  BankIcon,
  CashSpecialIcon,
  WalletIcon,
} from "lola-framework-ui-test";
import { PayoutMethodType } from "../interfaces/payout";
import type { ComposeSelection } from "../store/useComposeStore";
import type { IUserData } from "../shared/session/interfaces/session";
import { mockCatalogFor } from "../gallery/requestMock";
import { catalogNamedPayer } from "./payerCode";
import { bankAccountRowLabel } from "./bankAccountLabel";

const last4 = (value?: string) => (value ? value.slice(-4) : "");

const payerLabel = (
  code: string | undefined,
  destinationCountry = "MX",
): string | undefined => {
  if (!code) return undefined;
  const catalog = mockCatalogFor(destinationCountry);
  return (
    catalogNamedPayer(code, catalog.cashPayers) ??
    catalogNamedPayer(code, catalog.banks) ??
    code
  );
};

/** Transfer-row subtitle + icon from claim snapshot (locked or default). */
export function payoutRowFromClaim(
  userData: IUserData | undefined,
  t: TFunction<"fund-request">,
): { subtitle: string; icon: JSX.Element } {
  const pm = userData?.paymentMethod;
  switch (pm?.type) {
    case "cash": {
      const payer =
        payerLabel(pm.cash?.payer ?? pm.cash?.provider) ??
        pm.cash?.provider;
      return {
        subtitle: payer
          ? t("transfer.cashPickup", { payer })
          : t("transfer.payoutAccount"),
        icon: <CashSpecialIcon size={20} />,
      };
    }
    case "card": {
      const digits = last4(pm.card?.last4);
      return {
        subtitle: digits
          ? t("transfer.cardEnding", { last4: digits })
          : t("transfer.payoutAccount"),
        icon: <WalletIcon size={20} />,
      };
    }
    case "bank_account": {
      const digits = last4(pm.bankAccount?.accountNumber);
      const code = pm.bankAccount?.bankCode;
      const named = payerLabel(code);
      return {
        subtitle: bankAccountRowLabel({
          stored: pm.bankAccount?.bankName,
          catalogName: named && named !== code ? named : undefined,
          accountOrLast4: pm.bankAccount?.accountNumber,
          unnamedWithDigits: t("transfer.bankAccount", { last4: digits }),
          empty: t("transfer.payoutAccount"),
        }),
        icon: <BankIcon size={20} />,
      };
    }
    default:
      return {
        subtitle: t("transfer.payoutAccount"),
        icon: <BankIcon size={20} />,
      };
  }
}

/** Transfer-row subtitle + icon when payer may change payout in compose. */
export function payoutRowFromSelection(
  selection: ComposeSelection | null | undefined,
  t: TFunction<"fund-request">,
  userData?: IUserData,
): { subtitle: string; icon: JSX.Element } {
  if (!selection) {
    return {
      subtitle: t("transfer.payoutAccount"),
      icon: <BankIcon size={20} />,
    };
  }
  const { method, payout } = selection;
  switch (method) {
    case PayoutMethodType.CASH_PICKUP: {
      const payer = payerLabel(payout.payerId) ?? payout.payerId;
      return {
        subtitle: payer
          ? t("transfer.cashPickup", { payer })
          : t("transfer.payoutAccount"),
        icon: <CashSpecialIcon size={20} />,
      };
    }
    case PayoutMethodType.PUSH_TO_CARD:
      return {
        subtitle: payout.last4
          ? t("transfer.cardEnding", { last4: payout.last4 })
          : t("transfer.payoutAccount"),
        icon: <WalletIcon size={20} />,
      };
    case PayoutMethodType.BANK_ACCOUNT: {
      const tail = payout.accountNumber?.slice(-4) ?? payout.last4;
      const claimBank = userData?.paymentMethod?.bankAccount;
      const matchesClaim =
        Boolean(claimBank?.bankCode) &&
        payout.bankId?.toUpperCase() === claimBank?.bankCode?.toUpperCase();
      const stored = matchesClaim ? claimBank?.bankName : payout.bankId;
      const code = matchesClaim ? claimBank?.bankCode : payout.bankId;
      const named = payerLabel(code);
      return {
        subtitle: bankAccountRowLabel({
          stored,
          catalogName: named && named !== code ? named : undefined,
          accountOrLast4: payout.accountNumber ?? payout.last4,
          unnamedWithDigits: t("transfer.bankAccount", { last4: tail }),
          empty: t("transfer.payoutAccount"),
        }),
        icon: <BankIcon size={20} />,
      };
    }
    case PayoutMethodType.MOBILE_WALLET:
      return {
        subtitle: payout.phone ?? t("transfer.payoutAccount"),
        icon: <WalletIcon size={20} />,
      };
    default:
      return {
        subtitle: t("transfer.payoutAccount"),
        icon: <BankIcon size={20} />,
      };
  }
}

/** Picker default row title (method line, no "Default" suffix). */
export function defaultPayoutPickerTitle(
  userData: IUserData | undefined,
  t: TFunction<"fund-request">,
): string {
  return payoutRowFromClaim(userData, t).subtitle;
}
